import React, { useCallback, useEffect, useState } from "react";
import { useConfirm } from "@/hooks/useConfirm";
import ConfirmDialog from "@/components/ConfirmDialog";
import { api } from "@/lib/api";
import { CalendarDays, Check, Loader2, Link2Off, ExternalLink } from "lucide-react";
import { toast } from "sonner";

function Toggle({ id, checked, onChange, label, hint }) {
  return (
    <div className="flex items-start gap-3">
      <button
        type="button" role="switch" aria-checked={checked} aria-label={label}
        data-testid={`calendar-toggle-${id}`} onClick={() => onChange(!checked)}
        className={`mt-0.5 flex-shrink-0 w-10 h-5 border transition-colors ${
          checked ? "bg-brand/30 border-brand/50" : "bg-surface-2 border-line/20"}`}
      >
        <span className={`block w-4 h-4 mt-px transition-transform ${
          checked ? "translate-x-5 bg-brand" : "translate-x-px bg-fg/40"}`} />
      </button>
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-fg-muted leading-relaxed">{hint}</p>
      </div>
    </div>
  );
}

export default function CalendarSettings({ workspaceId }) {
  const { ask, confirmProps } = useConfirm();
  const [status, setStatus] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const r = await api.get("/calendar/status", { params: { workspace_id: workspaceId } });
      setStatus(r.data);
      if (r.data.connected) {
        const c = await api.get("/calendar/calendars", { params: { workspace_id: workspaceId } });
        setCalendars(c.data);
      }
    } catch { setStatus({ connected: false }); }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const connect = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/calendar/connect", { username: username.trim(), password },
        { params: { workspace_id: workspaceId } });
      setPassword("");
      toast.success("Calendar conectat");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Conectarea a eşuat");
    } finally { setBusy(false); }
  };

  const patch = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/calendar/preferences", payload,
        { params: { workspace_id: workspaceId } });
      setStatus(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nu s-a putut salva");
    } finally { setBusy(false); }
  };

  const disconnect = () => {
    ask({
      title: "Deconectezi calendarul?", destructive: true, confirmLabel: "Deconectează",
      description: "Parola stocată se șterge. Va trebui introdusă din nou.",
      testId: "confirm-disconnect-calendar", onConfirm: doDisconnect,
    });
  };

  const doDisconnect = async () => {
    setBusy(true);
    try {
      await api.delete("/calendar/connections", { params: { workspace_id: workspaceId } });
      setCalendars([]);
      toast.success("Calendar deconectat");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Deconectarea a eşuat");
    } finally { setBusy(false); }
  };

  if (!status) return null;

  return (
    <section className="bg-surface border border-line/10 mt-6" data-testid="calendar-settings">
      <div className="px-5 py-4 border-b border-line/10 flex items-center gap-2">
        <CalendarDays className={`w-4 h-4 ${status.connected ? "text-brand" : "text-fg-muted"}`} />
        <h3 className="font-bold tracking-tight">Calendar</h3>
        <span className="label-tiny ml-2">// INFOMANIAK</span>
        {status.connected && (
          <span data-testid="calendar-connected" className="ml-auto flex items-center gap-1.5 text-xs text-brand">
            <Check className="w-3.5 h-3.5" /> Conectat ca {status.username}
          </span>
        )}
      </div>

      <div className="p-5 space-y-4">
        {!status.connected ? (
          <form onSubmit={connect} className="space-y-3">
            <p className="text-xs text-fg-muted leading-relaxed">
              Conectează-ți contul Infomaniak. Fiecare membru folosește propriile credențiale —
              calendarul tău rămâne al tău.
            </p>
            <div>
              <label htmlFor="cal-user" className="label-tiny block mb-1.5">Utilizator Infomaniak</label>
              <input
                id="cal-user" data-testid="calendar-username" required value={username}
                onChange={(e) => setUsername(e.target.value.trim())}
                placeholder="ex. OR02058" className="input-flat mono w-full"
              />
              <p className="text-[11px] text-fg-muted mt-1">
                E login-ul Infomaniak, <strong>nu adresa de email</strong> — îl vezi în
                manager.infomaniak.com, în profilul tău.
              </p>
            </div>
            <div>
              <label htmlFor="cal-pass" className="label-tiny block mb-1.5">Parolă de aplicație</label>
              <input
                id="cal-pass" data-testid="calendar-password" type="password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="input-flat mono w-full"
              />
              <p className="text-[11px] text-fg-muted mt-1">
                Se generează din{" "}
                <a href="https://manager.infomaniak.com" target="_blank" rel="noopener noreferrer"
                   className="text-brand-2 hover:text-brand inline-flex items-center gap-1">
                  manager.infomaniak.com <ExternalLink className="w-3 h-3" /></a>{" "}
                → Securitate → Parole de aplicație. Nu folosi parola contului.
              </p>
            </div>
            <button type="submit" data-testid="calendar-connect" disabled={busy}
                    className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Conectează
            </button>
          </form>
        ) : (
          <>
            <div>
              <label htmlFor="cal-pick" className="label-tiny block mb-1.5">Calendar implicit</label>
              <select
                id="cal-pick" data-testid="calendar-pick" value={status.calendar_path || ""}
                onChange={(e) => patch({ calendar_path: e.target.value })} disabled={busy}
                className="input-flat text-sm w-full"
              >
                {calendars.map((c) => <option key={c.path} value={c.path}>{c.name}</option>)}
              </select>
            </div>

            <div className="space-y-4 pt-2">
              <Toggle
                id="create" label="Atena poate crea evenimente în numele meu"
                hint="Chiar și pornit, orice creare îți cere confirmarea în panou."
                checked={Boolean(status.atena_may_create)}
                onChange={(v) => patch({ atena_may_create: v })}
              />
              <Toggle
                id="reminders" label="Primesc reminder cu 15 minute înainte"
                hint="Atena îți scrie în privat. Un singur mesaj per eveniment."
                checked={Boolean(status.reminders)}
                onChange={(v) => patch({ reminders: v })}
              />
              <Toggle
                id="team" label="Anunță echipa în #general"
                hint="Doar evenimentele care au participanți. Cele fără rămân private — un calendar personal conține și programări la dentist."
                checked={Boolean(status.team_reminders)}
                onChange={(v) => patch({ team_reminders: v })}
              />
            </div>

            <button type="button" data-testid="calendar-disconnect" onClick={disconnect} disabled={busy}
                    className="text-sm flex items-center gap-1.5 px-3 py-1.5 border border-line/20 text-fg/70 hover:text-fg hover:border-line/40 disabled:opacity-50">
              <Link2Off className="w-3.5 h-3.5" /> Deconectează
            </button>
          </>
        )}
      </div>
      <ConfirmDialog {...confirmProps} />
    </section>
  );
}
